import type { VatRateSchema } from "@repo/validators";
import type { z } from "zod";
import { useEffect } from "react";
import { NotificationTypes, Permissions } from "@repo/database";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { VatRate } from "~/providers/general-provider";
import { api } from "~/lib/api";
import { Notification } from "~/providers/notification-provider";
import { useVatRateStore } from "~/stores/use-vat-rate-store";
import { usePermissionManager } from "./use-permission-manager";

export const useNotificationManager = () => {
  const utils = api.useUtils();
  const { mutate: createVatRate, isPending: isCreatingVatRate } =
    api.vatRate.createVatRate.useMutation();

  const { mutate: updateVatRate, isPending: isUpdatingVatRate } =
    api.vatRate.updateVatRate.useMutation();
  const { mutate: markAsSeen } = api.notification.markAsSeen.useMutation({
    async onMutate(ids) {
      // 1. Cancel any ongoing infinite queries
      await utils.notification.getPaginated.cancel();

      // 2. Get previous infinite query data
      const prevData = utils.notification.getPaginated.getInfiniteData({
        limit: 15,
      });

      // 3. Optimistically update infinite pages
      utils.notification.getPaginated.setInfiniteData(
        { limit: 15 },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) =>
                ids.includes(notification.id)
                  ? { ...notification, seenAt: new Date() }
                  : notification,
              ),
            })),
          };
        },
      );

      return { prevData };
    },
    onError(_err, _ids, context) {
      // 4. Rollback to previous data on error
      utils.notification.getPaginated.setInfiniteData(
        { limit: 15 },
        context?.prevData,
      );
    },
    onSettled() {
      // 5. Invalidate to refresh from server
      utils.notification.getPaginated.invalidate();
    },
  });

  const { mutate: archiveAllNotifications } =
    api.notification.archiveAll.useMutation({
      async onMutate() {
        await utils.notification.getPaginated.cancel();

        const prevData = utils.notification.getPaginated.getData();

        utils.notification.getPaginated.setData(undefined, (old) => {
          if (!old) return prevData;
          return [];
        });

        return { prevData };
      },
      onSuccess() {
        toast.success("Archived all notifications");
        void utils.notification.getPaginated.invalidate();
      },
    });

  const isLoading = isCreatingVatRate || isUpdatingVatRate;

  const { setVatRateFormOpen, setSelectedVatRate, setIsLoading } =
    useVatRateStore();

  const { hasPermission } = usePermissionManager();

  const { t } = useTranslation("common");

  const createVatRateHandler = async (
    vatRate: z.infer<ReturnType<typeof VatRateSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.create_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      createVatRate(vatRate, {
        onSuccess() {
          toast.success(t("create_item_success", { item: t("vat_rate") }));
          resolve(true);
        },
        onError() {
          toast.error(t("create_item_error", { item: t("vat_rate") }));
          resolve(false);
        },
      });
    });
  };

  const updateVatRateHandler = async (
    id: string,
    vatRate: z.infer<ReturnType<typeof VatRateSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_vat_rate,
      label: t("Permissions.update_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      updateVatRate(
        {
          id,
          ...vatRate,
        },
        {
          onSuccess() {
            toast.success(t("update_item_success", { item: t("vat_rate") }));
            resolve(true);
          },
          onError() {
            toast.error(t("update_item_error", { item: t("vat_rate") }));
            resolve(false);
          },
        },
      );
    });
  };

  const openVatRateForm = async (vatRate?: VatRate) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.view_vat_rate_label"),
    });

    if (!permission) return false;

    setSelectedVatRate(vatRate);
    setVatRateFormOpen(true);
  };

  const getLoadingState = () => {
    return isLoading;
  };

  const archiveAll = () => {
    return new Promise<boolean>((resolve) => {
      archiveAllNotifications(undefined, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const markAsSeenHandler = (ids: string[]) => {
    if (ids.length === 0) return;
    markAsSeen(ids);
  };

  const showNotification = (notification: Notification) => {
    if (
      notification.booking &&
      notification.type === NotificationTypes.new_booking
    ) {
      toast.info(t("Notifications.new_booking_short"), {
        duration: 5000,
      });
    }
    const prev = utils.notification.getAll.getData();
    void utils.notification.getAll.setData(undefined, (old) => {
      if (!old) return prev;
      return [...old, notification];
    });
  };

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return {
    createVatRate: createVatRateHandler,
    updateVatRate: updateVatRateHandler,
    getLoadingState,
    openVatRateForm,
    showNotification,
    archiveAll,
    markAsSeen: markAsSeenHandler,
  };
};
